import { Button, Card, CardContent, Grid } from '@mui/material'
import { getYear } from 'date-fns'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import useBoolean from 'utils/customHook/useBoolean'
import useSandreList from 'utils/customHook/useSandreList'
import { getFileExtension } from 'utils/FileUtils'
import i18n from 'simple-react-i18n'
import { isNil, orderBy, uniqBy } from 'lodash'
import Select from 'components/forms/Select'
import SimpleTabList from 'components/list/SimpleTabList'
import useActions from 'utils/customHook/useActions'
import { getLinks } from 'utils/StationUtils'
import DtoFile from 'components/file/dto/DtoFile'
import Input from 'components/forms/Input'
import ReferencialAction from 'referencial/action/ReferencialAction'
import ImportDialog from './ImportDialog'
import moment from 'moment'
import FileAccordion from './FileAccordion'
import ReferencialFileAction from './actions/ReferencialFileAction'

const DATE = 'date'
const FILE = 'file'
const ACTIVE = 'active'

const FilterFields = ({
    defaultFilter = {},
    onValidate = () => {},
    files = [],
}) => {
    const typePictures = useSandreList(SANDRE.TYPE_PHOTO)
    const typeDocuments = useSandreList(SANDRE.TYPE_DOCUMENT)

    const [searchValue, setSearchValue] = useState(defaultFilter.searchValue)
    const [extension, setExtension] = useState(defaultFilter.extension)
    const [naturePicture, setNaturePicture] = useState(defaultFilter.naturePicture)
    const [natureDocument, setNatureDocument] = useState(defaultFilter.natureDocument)
    const [year, setYear] = useState(defaultFilter.year)
    const [active, setActive] = useState(defaultFilter.active)


    const dispatch = useDispatch()
    useEffect(() => {
        dispatch(ReferencialAction.fetchSandreCodes())
    }, [dispatch])

    const activeOptions = useMemo(() => [
        { name: i18n.active, code: '1' },
        { name: i18n.inactive, code: '0' },
    ], [])

    const orderedTypePictures = useMemo(() => orderBy(typePictures, 'code'), [typePictures])
    const orderedTypeDocuments = useMemo(() => orderBy(typeDocuments, 'code'), [typeDocuments])

    const onSearch = () => {
        onValidate({
            searchValue,
            extension,
            naturePicture,
            natureDocument,
            year,
            active,
        })
    }

    const onReset = () => {
        setSearchValue('')
        setExtension()
        setNaturePicture()
        setNatureDocument()
        setYear()
        setActive()

        onValidate({ searchValue: '' })
    }

    const extensions = useMemo(() => uniqBy(files.map(f => {
        const ext = `.${getFileExtension(f.nom)?.toLowerCase()}`
        return { id: ext, label: ext }
    }), 'id'), [files])

    const years = useMemo(() => {
        const formattedYears = files.map(f => {
            if (isNil(f.dateMaj) || isNaN(f.dateMaj)) {
                return { id: -1, label: i18n.unknownDate }
            }

            const date = moment(Number(f.dateMaj))
            const y = getYear(date)

            return { id: y, label: `${y}` }
        })

        return orderBy(uniqBy(formattedYears, 'id'), 'id')
    }, [files])

    return (
        <Card>
            <CardContent>
                <Grid container spacing={1}>
                    <Grid size={2}>
                        <Input
                            title={i18n.search}
                            value={searchValue}
                            onChange={setSearchValue}
                            onEnterKeyPress={onSearch}
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.extensions}
                            options={extensions}
                            value={extension}
                            onChange={setExtension}
                            keyValue='name'
                            keyLabel='name'
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.pictureType}
                            options={orderedTypePictures}
                            value={naturePicture}
                            onChange={setNaturePicture}
                            keyValue='name'
                            keyLabel='name'
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.documentType}
                            options={orderedTypeDocuments}
                            value={natureDocument}
                            onChange={setNatureDocument}
                            keyValue='name'
                            keyLabel='name'
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.year}
                            options={years}
                            value={year}
                            onChange={setYear}
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.active}
                            options={activeOptions}
                            value={active}
                            onChange={setActive}
                            nullLabel='&nbsp;'
                        />
                    </Grid>
                    <Grid size={1} offset={10}>
                        <Button
                            onClick={onReset}
                            variant='outlined'
                            color='primary'
                            fullWidth
                        >
                            {i18n.reinit}
                        </Button>
                    </Grid>
                    <Grid size={1}>
                        <Button
                            onClick={onSearch}
                            variant='contained'
                            color='primary'
                            fullWidth
                        >
                            {i18n.search}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

FilterFields.propTypes = {
    defaultFilter: PropTypes.shape({}),
    onValidate: PropTypes.func,
    files: PropTypes.arrayOf(PropTypes.instanceOf(DtoFile)),
}

const ReferencialPicturesApp = ({
    code,
    path,
    type,
}) => {
    const [files, setFiles] = useState([])
    const [data, setData] = useState([])
    const [filter, setFilter] = useState({ searchValue: '' })

    const dispatch = useDispatch()
    const {
        hydrogeologicalEntities,
        distributionUnitLinks,
        installation,
        stationWatermass,
    } = useSelector(store => ({
        hydrogeologicalEntities: store.HydrogeologicalEntityReducer.hydrogeologicalEntities,
        distributionUnitLinks: store.DistributionUnitReducer.distributionUnitLinks,
        installation: store.InstallationReducer.installation,
        stationWatermass: store.StationReducer.stationWatermass,
    }), shallowEqual)


    const fetchData = () => dispatch(ReferencialFileAction.fetchReferencialFile(code, path)).then(json => setData(Array.isArray(json) ? json : []))

    useEffect(() => {
        fetchData()
    }, [code, dispatch])


    const {
        value: isEdit,
        setTrue: onEdit,
        setFalse: onRead,
    } = useBoolean(false)
    const {
        value: isNew,
        setTrue: onNewFileOpen,
        setFalse: onNewFileClose,
    } = useBoolean(false)

    useActions(() => {
        const objLink = {
            hydrogeologicalEntities,
            distributionUnitLinks,
            installation,
            stationWatermass,
        }
        const baseActions = {
            links: getLinks(data, objLink),
        }
        if (isEdit) {
            return {
                cancel: onRead,
                new: onNewFileOpen,
                ...baseActions,
            }
        }
        return {
            edit: onEdit,
            ...baseActions,
        }
    }, [isEdit, data, hydrogeologicalEntities, distributionUnitLinks, installation, stationWatermass])

    return (
        <Grid container spacing={1} padding={1} direction='column'>
            { (
                <>
                    <FilterFields
                        defaultFilter={filter}
                        onValidate={setFilter}
                        files={data}
                    />
                    <SimpleTabList
                        defaultTab={DATE}
                        tabs={[
                            {
                                constant: DATE,
                                label: i18n.date,
                                icon: 'insert_invitation',
                            },
                            {
                                constant: FILE,
                                label: i18n.files,
                                icon: 'insert_drive_file',
                            },
                            {
                                constant: ACTIVE,
                                label: i18n.active,
                                icon: 'remove_red_eye',
                            },
                        ]}
                    >
                        {
                            tab => {
                                return (
                                    <Grid container spacing={1}>
                                        <FileAccordion
                                            data={data}
                                            group={tab}
                                            filter={filter}
                                            isEdit={isEdit}
                                            setAllFiles={setFiles}
                                            refetchFiles={fetchData}
                                            type={type}
                                        />
                                    </Grid>
                                )
                            }
                        }
                    </SimpleTabList>
                    <ImportDialog
                        isOpen={isNew}
                        refetchFiles={fetchData}
                        onClose={onNewFileClose}
                        onValidate={fetchData}
                        files={files}
                        code={code}
                        type={type}
                    />
                </>
            )}
        </Grid>
    )
}

ReferencialPicturesApp.propTypes = {
    code: PropTypes.string,
    path: PropTypes.string,
    type: PropTypes.string,
}

export default ReferencialPicturesApp